import { Controller } from '@hotwired/stimulus';
import $ from 'jquery';
import select2 from 'select2';

// Initialize select2 for jQuery
select2($);

// Usage:
// Wrap your select element with a div that has the data-controller="select2" attribute, and add data-select2-target="select" to the select element.
// Then add data attributes to the select element to set options for the select2 plugin.
// If you want to have custom callbacks, then create a new controller that extends this one and override the connect() method to initialize select2 with your custom options and callbacks. Make sure to call super.connect() to initialize the base functionality.
// Create the new controller in ./frontend/controllers/select2/your_custom_controller.js
export default class extends Controller {
  connect() {
    this.initializeSelect2();
    this.cacheHandler = () => this.select2Instance?.destroy();
    this.frameRenderHandler = event => {
      // Frame history clones the outgoing frame before turbo:before-cache.
      if (event.target.contains(this.element)) this.select2Instance?.destroy();
    };
    this.restoreHandler = () => {
      // Permanent form elements can survive rendering without reconnecting.
      if (this.element.isConnected && !this.select2Instance) this.initializeSelect2();
    };
    document.addEventListener('turbo:before-cache', this.cacheHandler);
    document.addEventListener('turbo:before-frame-render', this.frameRenderHandler);
    document.addEventListener('turbo:frame-render', this.restoreHandler);
    document.addEventListener('turbo:render', this.restoreHandler);
  }

  disconnect() {
    document.removeEventListener('turbo:before-cache', this.cacheHandler);
    document.removeEventListener('turbo:before-frame-render', this.frameRenderHandler);
    document.removeEventListener('turbo:frame-render', this.restoreHandler);
    document.removeEventListener('turbo:render', this.restoreHandler);
    this.select2Instance?.destroy();
  }

  initializeSelect2() {
    const $select = this.selectElement();
    $select
      .select2({
        ...this.defaultOptions()
      })
      // Keep the form bridge separate from Select2's display-only change.select2 refresh.
      .on('change.select2Form', function () {
        // Dispatch the change event for vanilla JS listeners
        let event = new Event('change.select2', { bubbles: true }); // fire a native event
        this.dispatchEvent(event);
      });
    this.select2Instance = $select.data('select2');
    $select.on('select2:open.select2Lifecycle', () => {
      this.$scrollParents = $select.parents();
    });
    $select.on('select2:close.select2Lifecycle', () => this.releaseScrollHandlers());

    // Select2 can destroy an instance itself when Turbo moves its select into
    // a new controller, before the old controller disconnects. Tie cleanup to
    // the widget's destroy lifecycle so it cannot tear down the replacement.
    const instance = this.select2Instance;
    const destroy = instance.destroy.bind(instance);
    instance.destroy = () => {
      instance.close();
      // After DOM removal, close cannot find the original scrollable parents.
      this.releaseScrollHandlers();
      $select.off('change.select2Form .select2Lifecycle');
      destroy();
      this.select2Instance = null;
    };
  }

  releaseScrollHandlers() {
    if (this.select2Instance) {
      this.$scrollParents?.off(`scroll.select2.${this.select2Instance.id}`);
    }
    this.$scrollParents = null;
  }

  selectElement() {
    return $(this.element.querySelector('select'));
  }

  refresh() {
    this.selectElement().trigger('change.select2');
  }

  defaultOptions() {
    return {
      theme: 'bootstrap-5',
      placeholder: 'Please select an option',
      width: '100%',
      dropdownCssClass: ':all:',
      dropdownParent: $(this.element)
    };
  }
}
